import i18next, { type i18n as I18n } from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import { initReactI18next } from 'react-i18next';
import caCommon from './locales/en-CA/common.json';
import account from './locales/en-US/account.json';
import auth from './locales/en-US/auth.json';
import common from './locales/en-US/common.json';
import errors from './locales/en-US/errors.json';
import invite from './locales/en-US/invite.json';
import member from './locales/en-US/member.json';
import order from './locales/en-US/order.json';
import org from './locales/en-US/org.json';
import project from './locales/en-US/project.json';
import shell from './locales/en-US/shell.json';
import workspace from './locales/en-US/workspace.json';

export const defaultNS = 'common';
export const baseLanguage = 'en-US';
export const supportedLanguages = ['en-US', 'en-CA'] as const;
export type SupportedLanguage = (typeof supportedLanguages)[number];

/** The base catalogue; its shape types every `t()` key (see `i18next.d.ts`). */
export const baseResources = {
  common,
  account,
  auth,
  invite,
  member,
  order,
  org,
  project,
  shell,
  workspace,
  errors,
};

/** `en-CA` holds overrides only and falls back to `en-US` for everything else. */
const resources = {
  'en-US': baseResources,
  'en-CA': { common: caCommon },
};

/**
 * Builds an initialised i18next instance: bundled catalogues (no HTTP backend, so init is
 * synchronous), language taken from the browser on every visit and never stored.
 */
export function createI18n({
  throwOnMissingKey = false,
}: {
  throwOnMissingKey?: boolean;
} = {}): I18n {
  const instance = i18next.createInstance();
  void instance
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      resources,
      supportedLngs: [...supportedLanguages],
      fallbackLng: baseLanguage,
      load: 'currentOnly',
      defaultNS,
      ns: Object.keys(baseResources),
      initAsync: false,
      interpolation: { escapeValue: false },
      detection: { order: ['navigator'], caches: [] },
      saveMissing: throwOnMissingKey,
      missingKeyHandler: (_languages, namespace, key) => {
        throw new Error(`Missing translation: ${namespace}:${key}`);
      },
    });
  return instance;
}

/** The app's instance. Tests fail on any key that is not in the catalogue. */
export const i18n = createI18n({ throwOnMissingKey: import.meta.env.MODE === 'test' });
